import React, { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Copy, Check } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ColorPickerProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  suggestions?: string[];
}

export function ColorPicker({ label, value, onChange, suggestions = [] }: ColorPickerProps) {
  const [hexInput, setHexInput] = useState('');
  const [isValidHex, setIsValidHex] = useState(true);
  const [copiedHex, setCopiedHex] = useState(false);
  const [copiedHsl, setCopiedHsl] = useState(false);

  // Validate hex format (#RGB or #RRGGBB)
  const validateHex = (hex: string): boolean => {
    const hexPattern = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
    return hexPattern.test(hex);
  };

  // Normalize 3-digit hex to 6-digit hex
  const normalizeHex = (hex: string): string => {
    if (hex.length === 4) {
      return `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`;
    }
    return hex;
  };

  // Convert HSL string to hex for color input
  const hslToHex = (hsl: string) => {
    const [h, s, l] = hsl.split(' ').map(v => parseFloat(v.replace('%', '')));
    const c = (1 - Math.abs(2 * (l / 100) - 1)) * (s / 100);
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = (l / 100) - c / 2;
    
    let r = 0, g = 0, b = 0;
    if (h >= 0 && h < 60) {
      r = c; g = x; b = 0;
    } else if (h >= 60 && h < 120) {
      r = x; g = c; b = 0;
    } else if (h >= 120 && h < 180) {
      r = 0; g = c; b = x;
    } else if (h >= 180 && h < 240) {
      r = 0; g = x; b = c;
    } else if (h >= 240 && h < 300) {
      r = x; g = 0; b = c;
    } else if (h >= 300 && h < 360) {
      r = c; g = 0; b = x;
    }
    
    r = Math.round((r + m) * 255);
    g = Math.round((g + m) * 255);
    b = Math.round((b + m) * 255);
    
    return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
  };
  
  // Convert hex to HSL string
  const hexToHsl = (hex: string) => {
    const normalizedHex = normalizeHex(hex);
    const r = parseInt(normalizedHex.slice(1, 3), 16) / 255;
    const g = parseInt(normalizedHex.slice(3, 5), 16) / 255;
    const b = parseInt(normalizedHex.slice(5, 7), 16) / 255;
    
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0, s = 0, l = (max + min) / 2;
    
    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      
      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
      }
      h /= 6;
    }
    
    h = Math.round(h * 360);
    s = Math.round(s * 100);
    l = Math.round(l * 100);
    
    return `${h} ${s}% ${l}%`;
  };

  const handleHexInput = (hex: string) => {
    setHexInput(hex);
    const isValid = validateHex(hex);
    setIsValidHex(isValid);
    
    if (isValid) {
      onChange(hexToHsl(hex));
    }
  };

  const copyToClipboard = (text: string, type: 'hex' | 'hsl') => {
    navigator.clipboard.writeText(text);
    if (type === 'hex') {
      setCopiedHex(true);
      setTimeout(() => setCopiedHex(false), 2000);
    } else {
      setCopiedHsl(true);
      setTimeout(() => setCopiedHsl(false), 2000);
    }
  };

  const currentHex = hslToHex(value);

  // Common brand hex suggestions
  const hexSuggestions = [
    '#3B82F6', '#EF4444', '#10B981', '#F59E0B',
    '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'
  ];
  
  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium">{label}</Label>
      <div className="flex gap-2">
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="w-20 h-10 p-1"
              style={{ backgroundColor: currentHex }}
            >
              <span className="sr-only">Pick color</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80">
            <div className="space-y-4">
              {/* Visual Color Picker */}
              <input
                type="color"
                value={currentHex}
                onChange={(e) => onChange(hexToHsl(e.target.value))}
                className="w-full h-10 rounded border border-input cursor-pointer"
              />
              
              {/* Hex Input Field */}
              <div className="space-y-2">
                <Label className="text-xs font-medium">Hex Code</Label>
                <div className="flex gap-2">
                  <Input
                    value={hexInput}
                    onChange={(e) => handleHexInput(e.target.value)}
                    placeholder={currentHex}
                    className={cn(
                      "flex-1 font-mono text-xs",
                      !isValidHex && "border-destructive"
                    )}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(currentHex, 'hex')}
                    className="px-2"
                  >
                    {copiedHex ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                  </Button>
                </div>
                {!isValidHex && (
                  <p className="text-xs text-destructive">
                    Invalid hex format. Use #RGB or #RRGGBB
                  </p>
                )}
              </div>

              {/* Common Hex Suggestions */}
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Common Colors</Label>
                <div className="grid grid-cols-4 gap-2">
                  {hexSuggestions.map((hex, index) => (
                    <Button
                      key={index}
                      variant="outline"
                      className="w-full h-8 p-0 relative group"
                      style={{ backgroundColor: hex }}
                      onClick={() => onChange(hexToHsl(hex))}
                      title={hex}
                    >
                      <span className="sr-only">Use color {hex}</span>
                    </Button>
                  ))}
                </div>
              </div>

              {/* HSL Suggestions from props */}
              {suggestions.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Theme Suggestions</Label>
                  <div className="grid grid-cols-4 gap-2">
                    {suggestions.map((suggestion, index) => (
                      <Button
                        key={index}
                        variant="outline"
                        className="w-full h-8 p-0"
                        style={{ backgroundColor: hslToHex(suggestion) }}
                        onClick={() => onChange(suggestion)}
                      >
                        <span className="sr-only">Use color {suggestion}</span>
                      </Button>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Format Display */}
              <div className="space-y-2 pt-2 border-t">
                <div className="flex items-center justify-between">
                  <Label className="text-xs text-muted-foreground">HSL Value</Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(value, 'hsl')}
                    className="h-6 px-2"
                  >
                    {copiedHsl ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                  </Button>
                </div>
                <code className="text-xs bg-muted px-2 py-1 rounded font-mono block">
                  {value}
                </code>
              </div>
            </div>
          </PopoverContent>
        </Popover>
        
        <div className="flex-1 flex items-center">
          <code className="text-xs bg-muted px-2 py-1 rounded font-mono">
            {value}
          </code>
        </div>
      </div>
    </div>
  );
}